import React, { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import { useImagePreloader, useLazyLoading } from "./utils/imagePreloader";
import ErrorBoundary from "./components/ErrorBoundary";
import { ToastProvider } from "./components";
import { SessionProvider } from "./context/SessionProvider";
import { RouteChangeLoader } from "./components";
import ScrollToTop from "./components/ScrollToTop";
import { useTranslation } from "react-i18next";

// Lazy load router for better performance
const Router = lazy(() => import("./router"));

// Optimized loading component for mobile
const AppLoader: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-center h-screen bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500 mx-auto mb-4"></div>
        <p className="text-gray-600 text-sm">{t("app.loading")}</p>
      </div>
    </div>
  );
};

// Image Preloader Component - must be inside BrowserRouter
const ImagePreloader: React.FC = () => {
  const location = useLocation();
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Automatically preload images based on current route
  useImagePreloader(location.pathname);

  // Enable enhanced lazy loading for all images
  useLazyLoading();

  // Handle initial load optimization
  useEffect(() => {
    if (isInitialLoad) {
      // Remove initial loader after React takes control
      const timer = setTimeout(() => {
        document.body.classList.add("app-loaded");
        setIsInitialLoad(false);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isInitialLoad]);

  return null; // This component doesn't render anything
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <SessionProvider>
        <ToastProvider>
          <BrowserRouter>
            <ScrollToTop />
            <ImagePreloader />
            <RouteChangeLoader />
            <Suspense fallback={<AppLoader />}>
              <Router />
            </Suspense>
          </BrowserRouter>
        </ToastProvider>
      </SessionProvider>
    </ErrorBoundary>
  );
};

export default App;
